import { useEffect, useState } from 'react';
import { Plus, Edit2, Search, Building2, Mail, Phone, User, FileText } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { getBaseApi, unidadesApi } from '../api/backend';
import { Unidade } from '../types';
import { on } from 'events';

export function UnidadesPage() {
  const { isAdminRole } = useAuthStore();
  const navigate = useNavigate();
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUnidade, setEditingUnidade] = useState<Unidade | null>(null);

  useEffect(() => {
    if (!isAdminRole()) {
      navigate('/dashboard');
      return;
    }
    loadUnidades();
  }, [isAdminRole, navigate]);

  const loadUnidades = async () => {
    try {
      setLoading(true);
      const items = await unidadesApi.list()
      console.log({ items })
      setUnidades(items);
    } catch (error) {
      console.error('Error loading unidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUnidades = unidades.filter((u) =>
    u.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.cnpj && u.cnpj.includes(searchTerm)) ||
    (u.responsavel_nome && u.responsavel_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.responsavel_email && u.responsavel_email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isAdminRole()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Building2 className="w-8 h-8 text-primary-600" />
                Gerenciar Unidades
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Gerencie todas as unidades cadastradas no sistema
              </p>
            </div>
            <button
              onClick={() => navigate('/unidades/nova')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nova Unidade
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, código, CNPJ, responsável ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Unidades List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando unidades...</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredUnidades.map((unidade) => (
              <div
                key={unidade.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Logo */}
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-xl font-bold">
                      {unidade.logo_url ? (
                        <img
                          src={unidade.logo_url}
                          alt={unidade.nome}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        unidade.nome.charAt(0).toUpperCase()
                      )}
                    </div>

                    {/* Unidade Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {unidade.nome}
                        </h3>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          {unidade.codigo}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            unidade.ativo
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}
                        >
                          {unidade.ativo ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {unidade.cnpj && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <FileText className="w-4 h-4" />
                            <span className="font-medium">CNPJ:</span>
                            <span>{unidade.cnpj}</span>
                          </div>
                        )}

                        {unidade.responsavel_nome && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <User className="w-4 h-4" />
                            <span className="font-medium">Responsável:</span>
                            <span>{unidade.responsavel_nome}</span>
                          </div>
                        )}

                        {unidade.responsavel_email && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <Mail className="w-4 h-4" />
                            <span>{unidade.responsavel_email}</span>
                          </div>
                        )}

                        {unidade.responsavel_telefone && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <Phone className="w-4 h-4" />
                            <span>{unidade.responsavel_telefone}</span>
                          </div>
                        )}

                        {unidade.responsavel_cargo && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Cargo:</span>
                            <span>{unidade.responsavel_cargo}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => setEditingUnidade(unidade)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}

            {filteredUnidades.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {searchTerm ? 'Nenhuma unidade encontrada' : 'Nenhuma unidade cadastrada'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {editingUnidade && (
          <EditUnidadeModal
            unidade={editingUnidade}
            onClose={() => setEditingUnidade(null)}
            onSave={() => {
              setEditingUnidade(null);
              loadUnidades();
            }}
          />
        )}
      </div>
    </div>
  );
}

// Edit Unidade Modal Component
function EditUnidadeModal({
  unidade,
  onClose,
  onSave,
}: {
  unidade: Unidade;
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState({
    nome: unidade.nome,
    cnpj: unidade.cnpj || '',
    responsavel_nome: unidade.responsavel_nome || '',
    responsavel_cpf: unidade.responsavel_cpf || '',
    responsavel_email: unidade.responsavel_email || '',
    responsavel_telefone: unidade.responsavel_telefone || '',
    responsavel_cargo: unidade.responsavel_cargo || '',
    ativo: unidade.ativo,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await unidadesApi.update(unidade.id, formData);
      onSave();

      /*
      if (data.success) {
        onSave();
      } else {
        alert('Erro ao atualizar unidade: ' + data.error);
      }
      */
    } catch (error) {
      console.error('Error updating unidade:', error);
      alert('Erro ao atualizar unidade');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Editar Unidade</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nome da Unidade *
            </label>
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              CNPJ
            </label>
            <input
              type="text"
              value={formData.cnpj}
              onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nome do Responsável
            </label>
            <input
              type="text"
              value={formData.responsavel_nome}
              onChange={(e) => setFormData({ ...formData, responsavel_nome: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              CPF do Responsável
            </label>
            <input
              type="text"
              value={formData.responsavel_cpf}
              onChange={(e) => setFormData({ ...formData, responsavel_cpf: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.responsavel_email}
              onChange={(e) => setFormData({ ...formData, responsavel_email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefone
            </label>
            <input
              type="text"
              value={formData.responsavel_telefone}
              onChange={(e) => setFormData({ ...formData, responsavel_telefone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cargo do Responsável
            </label>
            <input
              type="text"
              value={formData.responsavel_cargo}
              onChange={(e) => setFormData({ ...formData, responsavel_cargo: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Unidade Ativa
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
